import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PAINTED, bySlug } from "@/lib/trolls";
import { playTrollSound } from "@/lib/trollSound";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rand = (a, b) => a + Math.random() * (b - a);
const MODES = ["face", "face", "wave", "wave", "giant"];
const HOLD = { face: 2600, wave: 3000, giant: 3400 };
const GOLD = "sepia(1) saturate(4) hue-rotate(-12deg) brightness(1.08) drop-shadow(0 0 18px rgba(255, 196, 60, 0.9))";
const SHADOW = "drop-shadow(0 14px 18px rgba(46, 42, 37, 0.35))";
const TEST = new URLSearchParams(window.location.search).get("peek");

// Hidden while the visitor types, has a dialog open, or the tab is in the background.
const busy = () => {
  const el = document.activeElement;
  return document.hidden || !!document.querySelector('[role="dialog"]') || ["INPUT", "TEXTAREA", "SELECT"].includes(el?.tagName) || !!el?.isContentEditable;
};

const makePeek = () => {
  const golden = TEST === "golden" || Math.random() < 0.06;
  const mode = TEST && MODES.includes(TEST) ? TEST : golden ? pick(["face", "wave"]) : pick(MODES);
  const slug = mode === "giant" ? "elvejotnen" : pick(PAINTED.filter((s) => s !== "elvejotnen"));
  const group = golden ? "golden" : mode === "giant" ? "giant" : "lines";
  return { id: Date.now(), slug, mode, golden, group, idx: Math.floor(Math.random() * 8), side: Math.random() < 0.5 ? "left" : "right", top: rand(22, 56) };
};

const useScheduler = (onPeek, enabled) => {
  useEffect(() => {
    if (!enabled) return undefined;
    let timer;
    let lastScroll = 0;
    let waiting = false;
    const wait = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(tick, ms);
    };
    function tick() {
      if (busy()) return wait(5000);
      if (!TEST && Date.now() - lastScroll > 15000) {
        waiting = true;
        return undefined;
      }
      const hold = onPeek();
      return wait(hold + (TEST ? rand(2000, 3000) : rand(20000, 40000)));
    }
    const onScroll = () => {
      const first = lastScroll === 0;
      lastScroll = Date.now();
      if (first && !TEST) wait(rand(6000, 10000));
      else if (waiting) {
        waiting = false;
        wait(1200);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    if (TEST) wait(1500);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled, onPeek]);
};


const layout = (mode, narrow) =>
  ({
    face: { height: "clamp(170px, 34vh, 330px)", shown: 50, tilt: 16 },
    wave: { height: "clamp(220px, 44vh, 430px)", shown: narrow ? 38 : 4, tilt: 0 },
    giant: { height: "min(100vh, 125vw)", shown: narrow ? 55 : 10, tilt: 3 },
  })[mode];

const MOTION = {
  face: { animate: { rotate: [0, 5, -3, 4, 0] }, transition: { delay: 0.5, duration: 2 } },
  wave: { animate: { y: [0, -10, 0, -10, 0, 0], rotate: [0, -6, 6, -6, 6, 0] }, transition: { delay: 0.5, duration: 1.9 } },
  giant: { animate: { scale: [1, 1.04, 1.02], y: [0, -8, 0] }, transition: { delay: 0.7, duration: 2.4 } },
};

const GoldSparkles = () => (
  <div className="absolute inset-0">
    {[["12%", "18%"], ["70%", "8%"], ["20%", "62%"], ["78%", "48%"]].map(([l, tp], i) => (
      <motion.span key={l} className="absolute text-[#F2C14E]" style={{ left: l, top: tp }} animate={{ opacity: [0, 1, 0], scale: [0.6, 1.2, 0.6] }} transition={{ duration: 1.2, delay: i * 0.3, repeat: Infinity }}>
        <Sparkles className="w-6 h-6" />
      </motion.span>
    ))}
  </div>
);

const Bubble = ({ peek, name }) => {
  const { t } = useLang();
  const p = t.peek;
  const text = peek.group === "golden" ? p.golden : p[peek.group][peek.idx % p[peek.group].length];
  const left = peek.side === "left";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.55, type: "spring", stiffness: 220, damping: 15 }}
      data-testid="troll-peek-bubble"
      className={`absolute w-max max-w-[46vw] sm:max-w-[260px] rounded-2xl border-2 px-4 py-3 shadow-journal-lg ${peek.golden ? "bg-[#FFF6DC] border-[#D19E59]" : "bg-paper border-ink/15"}`}
      style={{ top: peek.mode === "giant" ? "16%" : "4%", [left ? "left" : "right"]: "calc(100% - 6px)", transformOrigin: left ? "left bottom" : "right bottom" }}
    >
      {peek.golden && (
        <div className="flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#B37A4B]">
          <Sparkles className="w-3 h-3" /> {p.goldenTag}
        </div>
      )}
      <p className="font-display text-sm sm:text-base font-semibold text-ink leading-snug">{text}</p>
      <p className="mt-1 text-[11px] font-bold text-ink-muted">— {name}</p>
    </motion.div>
  );
};

const PeekTroll = ({ peek }) => {
  const cfg = layout(peek.mode, window.innerWidth < 640);
  const dir = peek.side === "left" ? -1 : 1;
  const hidden = { x: `${dir * 110}%`, rotate: 0 };
  const shown = { x: `${dir * cfg.shown}%`, rotate: -dir * cfg.tilt };
  const pos = peek.mode === "face" ? { top: `${peek.top}vh` } : { bottom: peek.mode === "giant" ? "-4vh" : 0 };
  return (
    <motion.div
      initial={hidden}
      animate={shown}
      exit={hidden}
      transition={{ type: "spring", stiffness: peek.mode === "giant" ? 70 : 120, damping: 17 }}
      aria-hidden="true"
      data-testid="troll-peek"
      data-mode={peek.mode}
      data-side={peek.side}
      data-golden={peek.golden ? "true" : "false"}
      data-slug={peek.slug}
      className="fixed z-[45] pointer-events-none"
      style={{ ...pos, [peek.side]: 0, height: cfg.height, transformOrigin: peek.side === "left" ? "bottom left" : "bottom right" }}
    >
      <div className="h-full" style={{ transform: dir === 1 ? "scaleX(-1)" : undefined }}>
        <motion.img src={`/trolls/${peek.slug}.webp`} alt="" draggable={false} {...MOTION[peek.mode]} className="h-full w-auto max-w-none select-none" style={{ filter: peek.golden ? GOLD : SHADOW }} />
      </div>
      {peek.golden && <GoldSparkles />}
      <Bubble peek={peek} name={bySlug(peek.slug).name} />
    </motion.div>
  );
};

export default function TrollPeek() {
  const reduced = useReducedMotion();
  const [peek, setPeek] = useState(null);
  const onPeek = useCallback(() => {
    const p = makePeek();
    const img = new Image();
    img.src = `/trolls/${p.slug}.webp`;
    img.decode().catch(() => {}).finally(() => {
      setPeek(p);
      playTrollSound(p.golden ? "golden" : p.mode === "giant" ? "giant" : "giggle");
      setTimeout(() => setPeek((cur) => (cur?.id === p.id ? null : cur)), HOLD[p.mode]);
    });
    return HOLD[p.mode];
  }, []);
  useScheduler(onPeek, !reduced);
  return <AnimatePresence>{peek && <PeekTroll key={peek.id} peek={peek} />}</AnimatePresence>;
}
